/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import { Container, Row, Col } from "react-awesome-styled-grid";
import { useParams } from "react-router-dom";
import { v4 } from "uuid";
import { Box, Spacer, Table, Text } from "@nordcloud/gnui";
import { Breadcrumbs, showSuccess } from "~/components";
import { useShowError } from "~/components/Error/useShowError";
import { InlineEditDropdown } from "~/components/InlineEdit/InlineEditDropdown";
import { InlineEditMultiSelect } from "~/components/InlineEdit/InlineEditMultiSelect";
import { InlineEditText } from "~/components/InlineEdit/InlineEditText";
import { InlinePrerequisites } from "~/components/InlineEdit/InlinePrerequisites";
import { serviceInitialLoadingState } from "~/components/Services/initialState";
import { ServiceLevelSelector } from "~/components/Services/ServiceLevelSelector";
import {
  ServiceIdRouteParams,
  ServiceLoading,
  ServiceSubmitValues,
} from "~/components/Services/types";
import { AddSLA } from "~/components/SLA/AddSLA";
import { SLAItem } from "~/components/SLA/SLAItem";
import { DetailRow } from "~/components/Utils/DetailRow";
import { navItems, serviceLineList } from "~/constants";
import { dateFormatWithHours } from "~/constants/timeFormats";
import {
  Employee,
  Prerequisite,
  PriceType,
  ProcessType,
  ProcessTypeDisplay,
  ServiceCommitment,
  serviceInitialState,
  Sla,
} from "~/models";
import { ACTIONS, Can, ROUTES, useAccess } from "~/routing";
import {
  checkValue,
  generateActionSuccessText,
  personToDropdown,
  useAxios,
  useDetailsNotFound,
} from "~/tools";
import { getEmployeeStatusColor } from "~/tools/display/displayEmployeeColor";
import { AddNewVersion } from "./AddNewVersion";

const priceTypeOptions = [
  { label: "Capacity Based Fee", value: PriceType.CAPACITY_BASED_FEE },
  { label: "Included in Base Fee", value: PriceType.INCLUDED_IN_BASE_FEE },
  { label: "Time and Material", value: PriceType.TIME_AND_MATERIAL },
  { label: "Custom", value: PriceType.CUSTOM },
];

const processTypeOptions = [
  {
    label: ProcessTypeDisplay.INCIDENT_MANAGEMENT,
    value: ProcessType.INCIDENT_MANAGEMENT,
  },
  {
    label: ProcessTypeDisplay.SERVICE_REQUEST_MANAGEMENT,
    value: ProcessType.SERVICE_REQUEST_MANAGEMENT,
  },
  { label: ProcessTypeDisplay.PATCHING, value: ProcessType.PATCHING },
  { label: ProcessTypeDisplay.BACKUP, value: ProcessType.BACKUP },
  {
    label: ProcessTypeDisplay.CHANGE_MANAGEMENT,
    value: ProcessType.CHANGE_MANAGEMENT,
  },
  {
    label: ProcessTypeDisplay.PROBLEM_MANAGEMENT,
    value: ProcessType.PROBLEM_MANAGEMENT,
  },
];

type PrerequisiteList = Array<Prerequisite>;

// eslint-disable-next-line sonarjs/cognitive-complexity
function ServiceCommitmentDetails() {
  const state = useParams<ServiceIdRouteParams>();
  const { serviceId } = state;
  const [service, setService] = React.useState<ServiceCommitment>(
    serviceInitialState
  );
  const [sla, setSla] = React.useState<Sla[]>([]);
  const [list, setList] = React.useState<PrerequisiteList>([]);
  const [employees, setEmployees] = React.useState<Employee[]>([]);

  const { showError } = useShowError();
  const { Axios } = useAxios();
  const { detailsNotFound } = useDetailsNotFound();
  const [
    isPrerequisitesEditMode,
    setIsPrerequisitesEditMode,
  ] = React.useState<boolean>(false);
  const mounted = React.useRef(true);

  const { hasAccess: canEdit } = useAccess(ACTIONS.EDIT_SERVICE_COMMITMENT);

  const fetchService = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(`/catalog/services/${serviceId}`);
      setService(data);

      type PrerequisitesItem = { text: string };
      const mapped = data?.prerequisites?.map((text: PrerequisitesItem) => {
        return { id: v4(), ...text };
      });
      data?.prerequisites && setList(mapped);
    } catch (e) {
      detailsNotFound(
        ROUTES.serviceCommitments.index,
        navItems.serviceCommitments
      );
    }
  }, [Axios, detailsNotFound, service?.serviceLevel?.id, serviceId]);

  React.useEffect(() => {
    fetchService();

    return () => {
      mounted.current = false;
    };
  }, [fetchService, isPrerequisitesEditMode]);

  const fetchSLA = React.useCallback(
    async (id?: string) => {
      try {
        const { data } = await Axios().get(
          `/catalog/services/${id || serviceId}/slas`
        );
        setSla(data.slas);
      } catch (e) {
        showError(e.response);
      }
    },
    [Axios, serviceId, showError]
  );

  React.useEffect(() => {
    mounted.current && fetchSLA(serviceId);
  }, [fetchSLA, serviceId]);

  const fetchEmployees = React.useCallback(async () => {
    try {
      const { data } = await Axios().get("/search/employees");
      setEmployees(data?.searchResults);
    } catch (e) {
      showError(e.response);
    }
  }, [Axios, showError]);

  React.useEffect(() => {
    mounted.current && fetchEmployees();
  }, [fetchEmployees]);

  const breadcrumbLabels = service
    ? [{ value: service?.id, label: service?.name }]
    : undefined;

  const [isLoading, setIsLoading] = React.useState<ServiceLoading>(
    serviceInitialLoadingState
  );

  const onSubmit = async (values: ServiceSubmitValues) => {
    const organizeValues = {
      name: values.name,
      description: values.description,
      version: checkValue(values.version),
      ownerId: checkValue(values.ownerId) || checkValue(service?.owner?.id),
      prerequisites: values.prerequisites,
      pricingType: values.pricingType,
      serviceLevelId: values.serviceLevelId || service.serviceLevel?.id,
      processType: values?.processType,
      serviceLines: values.serviceLines,
    };

    try {
      await Axios().put(`/catalog/services/${serviceId}`, organizeValues);
      showSuccess(generateActionSuccessText(`Service`)()("updated")());
      fetchService();
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(serviceInitialLoadingState);
    }
  };

  return (
    <Container>
      <Breadcrumbs title={service?.name || ""} labels={breadcrumbLabels}>
        <Can action={ACTIONS.ADD_SERVICE_VERSION}>
          {service && <AddNewVersion service={service} refetchSLA={fetchSLA} />}
        </Can>
        <Can action={ACTIONS.ADD_SERVICE_SLA}>
          <AddSLA id={serviceId} endpoint="services" refetch={fetchSLA} />
        </Can>
      </Breadcrumbs>

      <Box spacing="spacing04">
        <Row>
          <Col xs={8} sm={8} md={4} lg={5}>
            <Text weight="medium">Service Details</Text>
            <DetailRow title="Name" minWidth="7rem">
              <InlineEditText
                name="name"
                defaultValue={service.name}
                onSubmit={(name) => onSubmit({ ...service, name })}
                canEdit={canEdit}
              />
            </DetailRow>

            <DetailRow title="Version" minWidth="7rem">
              <InlineEditText
                name="version"
                defaultValue={service.version}
                onSubmit={(version) => onSubmit({ ...service, version })}
                canEdit={canEdit}
              />
            </DetailRow>

            <DetailRow title="Owner" minWidth="7rem">
              <InlineEditDropdown
                name="ownerId"
                defaultValue={service.owner?.id || ""}
                options={employees.map(personToDropdown)}
                onSubmit={(ownerId) => onSubmit({ ...service, ownerId })}
                canEdit={canEdit}
                width="80%"
                color={getEmployeeStatusColor(
                  employees.filter((emp) => emp.id === service.owner?.id)[0]
                )}
              />
            </DetailRow>

            <DetailRow title="Pricing type" minWidth="7rem">
              <InlineEditDropdown
                name="pricingType"
                defaultValue={service.pricingType}
                options={priceTypeOptions}
                onSubmit={(pricingType) =>
                  onSubmit({ ...service, pricingType })
                }
                canEdit={canEdit}
                width="80%"
              />
            </DetailRow>

            <DetailRow title="Service level" minWidth="7rem">
              <ServiceLevelSelector
                service={service}
                onSubmit={onSubmit}
                canEdit={canEdit}
              />
            </DetailRow>

            <DetailRow title="Process type" minWidth="7rem">
              <InlineEditDropdown
                name="processType"
                defaultValue={service.processType || ""}
                options={processTypeOptions}
                onSubmit={(processType) =>
                  onSubmit({ ...service, processType })
                }
                canEdit={canEdit}
                width="80%"
              />
            </DetailRow>

            <DetailRow title="Service lines" minWidth="7rem">
              <InlineEditMultiSelect
                options={serviceLineList}
                defaultValue={service.serviceLines || []}
                onSubmit={(serviceLines) =>
                  onSubmit({ ...service, serviceLines })
                }
                canEdit={canEdit}
                width="18rem"
              />
            </DetailRow>
          </Col>

          <Col xs={8} sm={8} md={4} lg={7}>
            <Box css={{ marginTop: "auto" }} boxStyle="lightGrey">
              <Text size="sm" weight="medium">
                History of Editing
              </Text>
              <Table striped>
                <Table.tbody>
                  <Table.tr>
                    <Table.td>Created by:</Table.td>
                    <Table.td data-cy="service-created-author">
                      {service?.log?.created?.author}
                    </Table.td>
                    <Table.td data-cy="service-created-time">
                      {dayjs(service?.log?.created?.time).format(
                        `${dateFormatWithHours}`
                      )}
                    </Table.td>
                  </Table.tr>
                  <Table.tr>
                    <Table.td>Modified by:</Table.td>
                    <Table.td data-cy="service-modify-author">
                      {service?.log?.modified?.author}
                    </Table.td>
                    <Table.td data-cy="service-modify-time">
                      {dayjs(service?.log?.modified?.time).format(
                        `${dateFormatWithHours}`
                      )}
                    </Table.td>
                  </Table.tr>
                </Table.tbody>
              </Table>
            </Box>
          </Col>

          <Col>
            <Spacer height=".5rem" />
            <DetailRow title="Description" minWidth="7rem">
              <InlineEditText
                textArea
                name="description"
                defaultValue={service.description}
                onSubmit={(description) =>
                  onSubmit({ ...service, description })
                }
                canEdit={canEdit}
              />
            </DetailRow>
          </Col>
        </Row>
      </Box>

      {sla.length !== 0 && (
        <Box spacing="spacing04">
          <Text weight="medium">SLA</Text>
          {sla?.map((s: Sla) => (
            <SLAItem
              key={s?.id}
              sla={s}
              refetch={fetchSLA}
              serviceId={serviceId}
            />
          ))}
        </Box>
      )}

      <InlinePrerequisites
        item={service}
        property="prerequisites"
        onSubmit={onSubmit}
        list={list}
        setList={setList}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        isPrerequisitesEditMode={isPrerequisitesEditMode}
        setIsPrerequisitesEditMode={setIsPrerequisitesEditMode}
      />
    </Container>
  );
}

export { ServiceCommitmentDetails };
